import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaIceCream, FaShoppingBag, FaTruck } from 'react-icons/fa';
import { saveOrder } from '../services/db';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #fff6f6 0%, #ffe4e4 100%);
  padding-top: 80px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 300px;
    background: linear-gradient(135deg, #ff6b6b 0%, #ff466a 100%);
    transform: skewY(-6deg);
    transform-origin: top left;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  color: white;
  position: relative;
`;

const Title = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Description = styled.p`
  color: #666;
  font-size: 1.1rem;
  margin-bottom: 2rem;
  text-align: center;
`;

const OrderContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const OrderSummary = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  height: fit-content;
  position: sticky;
  top: 100px;
`;

const SummaryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0f0f0;
`;

const SummaryIcon = styled(FaShoppingBag)`
  font-size: 1.8rem;
  color: #ff6b6b;
`;

const SummaryTitle = styled.h2`
  font-size: 1.8rem;
  color: #333;
  margin: 0;
`;

const OrderItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid #f0f0f0;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateX(5px);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const ItemDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const IceCreamIcon = styled(FaIceCream)`
  color: #ff6b6b;
  font-size: 1.5rem;
`;

const ItemInfo = styled.div``;

const ItemName = styled.h3`
  font-size: 1.2rem;
  color: #333;
  margin: 0;
`;

const ItemQuantity = styled.p`
  color: #666;
  margin: 0.2rem 0 0 0;
  font-size: 0.9rem;
`;

const ItemPrice = styled.span`
  color: #ff6b6b;
  font-weight: 600;
  font-size: 1.1rem;
`;

const TotalPrice = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1.5rem;
  margin-top: 1.5rem;
  border-top: 2px solid #f0f0f0;
  font-weight: 600;
  font-size: 1.3rem;
  color: #333;
`;

const OrderForm = styled.form`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0f0f0;
`;

const SectionIcon = styled.div`
  font-size: 1.5rem;
  color: #ff6b6b;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  color: #333;
  margin: 0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #555;
  font-weight: 500;
  font-size: 0.95rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem;
  border: 2px solid #f0f0f0;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #ff6b6b;
    box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.1);
  }

  &::placeholder {
    color: #aaa;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.8rem;
  border: 2px solid #f0f0f0;
  border-radius: 10px;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #ff6b6b;
    box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.1);
  }

  &::placeholder {
    color: #aaa;
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #ff6b6b 0%, #ff466a 100%);
  color: white;
  border: none;
  padding: 1.2rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  margin-top: 2rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(255, 107, 107, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const NoOrderMessage = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  color: #666;
  font-size: 1.2rem;

  h2 {
    color: #ff6b6b;
    margin-bottom: 1rem;
  }

  p {
    margin-bottom: 2rem;
  }
`;

const BackToMenuButton = styled.button`
  background: #ff6b6b;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #ff5252;
    transform: translateY(-2px);
  }
`;

const ErrorMessage = styled.p`
  color: #ff6b6b;
  font-size: 1rem;
  margin-bottom: 1rem;
`;

const Order = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const orderDetails = location.state?.orderDetails || { items: [], total: 0 };
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    specialInstructions: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    const totalOrderPrice = orderDetails.items.reduce((total, item) => total + item.totalPrice, 0);
    
    const newOrder = {
      customerName: formData.name,
      items: orderDetails.items || [],
      total: totalOrderPrice,
      status: 'pending',
      address: formData.address,
      phone: formData.phone,
      email: formData.email,
      specialInstructions: formData.specialInstructions
    };

    try {
      const savedOrder = await saveOrder(newOrder);
      navigate('/order-success', { state: { orderId: savedOrder.id } });
    } catch (error) {
      console.error('Error saving order:', error);
      setError('Failed to submit order. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!orderDetails.items || orderDetails.items.length === 0) {
    return (
      <PageContainer>
        <ContentWrapper>
          <NoOrderMessage>
            <h2>Your Cart is Empty</h2>
            <p>Please select some delicious ice cream from our menu first!</p>
            <BackToMenuButton onClick={() => navigate('/menu')}>
              Browse Menu
            </BackToMenuButton>
          </NoOrderMessage>
        </ContentWrapper>
      </PageContainer>
    );
  }

  const totalOrderPrice = orderDetails.items.reduce((total, item) => total + item.totalPrice, 0);

  return (
    <PageContainer>
      <ContentWrapper>
        <Header>
          <Title>Complete Your Order</Title>
          <Description>
            You're just a few steps away from enjoying our delicious ice cream!
          </Description>
        </Header>

        <OrderContainer>
          <OrderSummary>
            <SummaryHeader>
              <SummaryIcon />
              <SummaryTitle>Order Summary</SummaryTitle>
            </SummaryHeader>
            {orderDetails.items.map((item) => (
              <OrderItem key={item.id}>
                <ItemDetails>
                  <IceCreamIcon />
                  <ItemInfo>
                    <ItemName>{item.name}</ItemName>
                    <ItemQuantity>Quantity: {item.quantity}</ItemQuantity>
                  </ItemInfo>
                </ItemDetails>
                <ItemPrice>${item.totalPrice.toFixed(2)}</ItemPrice>
              </OrderItem>
            ))}
            <TotalPrice>
              <span>Total Amount:</span>
              <span>${totalOrderPrice.toFixed(2)}</span>
            </TotalPrice>
          </OrderSummary>

          <OrderForm onSubmit={handleSubmit}>
            {error && <ErrorMessage>{error}</ErrorMessage>}
            <FormSection>
              <SectionHeader>
                <SectionIcon>
                  <FaTruck />
                </SectionIcon>
                <SectionTitle>Delivery Information</SectionTitle>
              </SectionHeader>
              
              <FormGrid>
                <FormGroup>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="John Doe"
                    required
                    disabled={submitting}
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="john@example.com"
                    required
                    disabled={submitting}
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="(123) 456-7890"
                    required
                    disabled={submitting}
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="address">Delivery Address</Label>
                  <Input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="123 Main St, City, State, ZIP"
                    required
                    disabled={submitting}
                  />
                </FormGroup>

                <FormGroup full>
                  <Label htmlFor="specialInstructions">Special Instructions</Label>
                  <TextArea
                    id="specialInstructions"
                    name="specialInstructions"
                    value={formData.specialInstructions}
                    onChange={handleInputChange}
                    placeholder="Any special delivery instructions..."
                    disabled={submitting}
                  />
                </FormGroup>
              </FormGrid>
            </FormSection>

            <SubmitButton type="submit" disabled={submitting}>
              {submitting ? 'Placing Order...' : 'Place Order'}
            </SubmitButton>
          </OrderForm>
        </OrderContainer>
      </ContentWrapper>
    </PageContainer>
  );
};

export default Order;
